import { useCallback, useMemo } from "react";

import { getIntlLocale } from "./config";
import { useLocale } from "./useTranslations";

/**
 * Localised language names straight from CLDR, the same data Flutter's intl and
 * every other ICU consumer reads. The option lists in lib/select-options carry
 * ISO 639-1 codes, so there is no reason to hand-maintain 184 translations --
 * and adding a third locale needs no work at all.
 *
 * Note this also changes the English labels, because CLDR is the authority on
 * them: "Abkhaz" becomes "Abkhazian", "Tagalog" becomes "Filipino", and the
 * awkward "Spanish; Castilian" becomes plain "Spanish".
 *
 * Codes CLDR does not recognise fall back to the label the list already had.
 */
export function useLanguageName() {
  const locale = useLocale();

  const displayNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([getIntlLocale(locale)], {
        type: "language",
      });
    } catch {
      // Older runtimes without Intl.DisplayNames keep the existing labels.
      return null;
    }
  }, [locale]);

  return useCallback(
    (code: string, fallback: string) => {
      if (!displayNames || !code) return fallback;
      try {
        return displayNames.of(code) ?? fallback;
      } catch {
        return fallback;
      }
    },
    [displayNames],
  );
}
